import React, { useEffect, useState } from "react";
import { apiRequest } from "../utils/api";
import { useNavigate } from 'react-router-dom';
import "../styles/DailySalesPage.css";

const CATEGORY_LIST_NORMAL = [
  "1F-PRIZE", "1F-TOY", "1F-GOODS",
  "2F-PRIZE", "2F-TOY", "2F-GOODS",
  "GACHA", "ETC"
];

const CATEGORY_LIST_TAX_EXEMPT = [
  "1F-PRIZE", "1F-TOY", "1F-GOODS",
  "2F-PRIZE", "2F-TOY", "2F-GOODS",
  "ETC"  // GACHA を除外
];

const TARGET_CATEGORIES = [
  "1F-PRIZE", "1F-TOY", "1F-GOODS",
  "2F-PRIZE", "2F-TOY", "2F-GOODS"
];

const CATEGORY_ID_MAP = {
  "1F-PRIZE": 1,
  "1F-TOY": 2,
  "1F-GOODS": 6,
  "2F-PRIZE": 3,
  "2F-TOY": 4,
  "2F-GOODS": 6
};

const DailySalesPage = () => {
  const [data, setData] = useState({});
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [availableYears, setAvailableYears] = useState([]);
  const navigate = useNavigate();// ページ移動

  useEffect(() => {
    const fetchData = async () => {
      const res = await apiRequest(`inventory/daily_sales_by_category_type/?year=${year}&month=${month}`);
      setData(res);

      // 年のリストを取得（データのキーから）
      const years = Object.keys(res)
        .map((d) => Number(d.split("-")[0]))
        .filter((y, i, arr) => arr.indexOf(y) === i)
        .sort((a, b) => a - b);

      setAvailableYears(years);
    };
    fetchData();
  }, [year, month]);  

  const formatDateShort = (dateStr) => {
    const [, m, d] = dateStr.split("-");
    return `${parseInt(m)}-${parseInt(d)}`;
  };

  const getValue = (cat, type, dayData) => {
    const val = dayData[type]?.[cat];
    return {
      total: val?.total || 0,
      count: val?.count || 0
    };
  };

  const sumTotal = (group) => {
    return Object.values(group || {}).reduce((acc, v) => ({
      total: acc.total + (v.total || 0),
      count: acc.count + (v.count || 0)
    }), { total: 0, count: 0 });
  };

  const computeMonthlyTotals = () => {
    const result = {
      normal: {},
      tax_exempt: {},
    };

    Object.values(data).forEach(dayData => {
      ["normal", "tax_exempt"].forEach(type => {
        const list = type === "normal" ? CATEGORY_LIST_NORMAL : CATEGORY_LIST_TAX_EXEMPT;
        list.forEach(cat => {
          const val = dayData?.[type]?.[cat];
          if (val) {
            if (!result[type][cat]) {
              result[type][cat] = { total: 0, count: 0 };
            }
            result[type][cat].total += val.total;
            result[type][cat].count += val.count;
          }
        });
      });
    });

    return result;
  };

  const renderHeader = () => (
    <thead>
      <tr>
        <th rowSpan="2" className="dailysalespage-table-date">日付</th>
        <th rowSpan="2">売上合計</th>
        <th rowSpan="2">件数</th>
        <th colSpan={TARGET_CATEGORIES.length}>カテゴリ別合計</th>
        <th colSpan={CATEGORY_LIST_NORMAL.length}>通常販売</th>
        <th colSpan={CATEGORY_LIST_TAX_EXEMPT.length}>免税販売</th>
      </tr>
      <tr>
        {TARGET_CATEGORIES.map((cat) => (
          <th key={`target-h-${cat}`}>{cat}</th>
        ))}
        {CATEGORY_LIST_NORMAL.map((cat) => (
          <th key={`n-${cat}`}>{cat}</th>
        ))}
        {CATEGORY_LIST_TAX_EXEMPT.map((cat) => (
          <th key={`t-${cat}`}>{cat}</th>
        ))}
      </tr>
    </thead>
  );

  const renderRows = () => {
    return Object.entries(data).map(([date, dayData]) => {
      const totalNormal = sumTotal(dayData.normal);
      const totalTaxExempt = sumTotal(dayData.tax_exempt);
      const grandTotal = totalNormal.total + totalTaxExempt.total;
      const grandCount = totalNormal.count + totalTaxExempt.count;

      return (
        <tr key={date}>
          <td>{formatDateShort(date)}</td>
          <td>¥{grandTotal.toLocaleString()}</td>
          <td>{grandCount}</td>

          {TARGET_CATEGORIES.map((cat) => {
            const normal = getValue(cat, "normal", dayData).total;
            const exempt = getValue(cat, "tax_exempt", dayData).total;
            const total = normal + exempt;
            const categoryId = CATEGORY_ID_MAP[cat];
            const clickedDate = new Date(date);
            const day = clickedDate.getDate();

            return (
              <td key={`target-${date}-${cat}`}>
                <div
                  onClick={() => {
                    if (total > 0 && categoryId) {
                      navigate(`/SalesSummaryPage?year=${year}&month=${month}&day=${day}&category_id=${categoryId}`);
                    }
                  }}
                  style={{ cursor: total > 0 ? 'pointer' : 'default', textDecoration: total > 0 ? 'underline' : 'none' }}
                >
                  {total > 0 ? `¥${total.toLocaleString()}` : "-"}
                </div>
              </td>
            );
          })}

          {CATEGORY_LIST_NORMAL.map((cat) => {
            const { total, count } = getValue(cat, "normal", dayData);
            if (cat === "GACHA" || cat === "ETC") {
              return (
                <td key={`normal-${date}-${cat}`}>
                  {total ? `¥${total.toLocaleString()}` : "-"}
                </td>
              );
            }
            return (
              <td key={`normal-${date}-${cat}`}>
                {total ? `¥${total.toLocaleString()} (${count})` : "-"}
              </td>
            );
          })}

          {CATEGORY_LIST_TAX_EXEMPT.map((cat) => {
            const { total, count } = getValue(cat, "tax_exempt", dayData);
            if (cat === "ETC") {
              return (
                <td key={`taxex-${date}-${cat}`}>
                  {total ? `¥${total.toLocaleString()}` : "-"}
                </td>
              );
            }
            return (
              <td key={`taxex-${date}-${cat}`}>
                {total ? `¥${total.toLocaleString()} (${count})` : "-"}
              </td>
            );
          })}
        </tr>
      );
    });
  };



  const renderFooter = () => {
    const totals = computeMonthlyTotals();
    const totalNormal = sumTotal(totals.normal);
    const totalTaxExempt = sumTotal(totals.tax_exempt);
    const grandTotal = totalNormal.total + totalTaxExempt.total;
    const grandCount = totalNormal.count + totalTaxExempt.count;

    return (
      <>
        {/* 金額行 */}
        <tr className="dailysalespage-table-total-row-amount-row">
          <td rowSpan="2">合計</td>
          <td rowSpan="2">¥{grandTotal.toLocaleString()}</td>
          <td rowSpan="2">{grandCount}</td>

          {TARGET_CATEGORIES.map((cat) => {
            const normal = totals.normal?.[cat]?.total || 0;
            const exempt = totals.tax_exempt?.[cat]?.total || 0;
            const total = normal + exempt;
            return (
              <td key={`footer-target-${cat}`} rowSpan="2">
                {total > 0 ? `¥${total.toLocaleString()}` : "-"} 
              </td>
            );
          })}

          {CATEGORY_LIST_NORMAL.map((cat) => {
            const val = totals.normal?.[cat];
            return (
              <td key={`footer-n-${cat}`}>
                {val ? `¥${val.total.toLocaleString()}` : "-"}
              </td>
            );
          })}

          {CATEGORY_LIST_TAX_EXEMPT.map((cat) => {
            const val = totals.tax_exempt?.[cat];
            return (
              <td key={`footer-t-${cat}`}>
                {val ? `¥${val.total.toLocaleString()}` : "-"}
              </td>
            );
          })}
        </tr>

        {/* 件数行 */}
        <tr className="dailysalespage-table-total-row-count-row">
          {CATEGORY_LIST_NORMAL.map((cat) => {
            const val = totals.normal?.[cat];
            const count = val?.count ?? 0;
            if (cat === "GACHA" || cat === "ETC") return <td key={`footer-nc-${cat}`}></td>;
            return <td key={`footer-nc-${cat}`}>({count})</td>;
          })}

          {CATEGORY_LIST_TAX_EXEMPT.map((cat) => {
            const val = totals.tax_exempt?.[cat];
            const count = val?.count ?? 0;
            if (cat === "ETC") return <td key={`footer-tc-${cat}`}></td>;
            return <td key={`footer-tc-${cat}`}>({count})</td>;
          })}
        </tr>
      </>
    );
  };


  return (
    <div className="dailysalespage-wrapper">
      <div className="dailysalespage-controls">
        <div>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}年</option>
            ))}
          </select>
        </div>

        <div>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}>
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}月</option>
            ))}
          </select>
        </div>

        <button onClick={() => setYear((prev) => prev - 1)}>
          ⬅ 前年同月
        </button>

        <button onClick={() => {
          setMonth((prevMonth) => {
            if (prevMonth === 1) {
              setYear((prevYear) => prevYear - 1);
              return 12;
            }
            return prevMonth - 1;
          });
        }}>
          ⬅ 前月
        </button>

        <button onClick={() => {
          setMonth((prevMonth) => {
            if (prevMonth === 12) {
              setYear((prevYear) => prevYear + 1);
              return 1;
            }
            return prevMonth + 1;
          });
        }}>
          翌月 ➡
        </button>
      </div>

      <h2>{year}年 {month}月 日別売上</h2>

      <div className="dailysalespage-tablewrapper">
        <table className="dailysalespage-table">
          {renderHeader()}
          <tbody>
            <tr className="dailysalespage-table-separator-row">
              <td colSpan={999}>&nbsp;</td>
            </tr>
            {renderFooter()}
            <tr className="dailysalespage-table-separator-row">
              <td colSpan={999}>&nbsp;</td>
            </tr>
            {renderRows()}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DailySalesPage;
